import React, { useState, useEffect, useCallback } from 'react';
import { API } from './App';

const mono = { fontFamily: "'DM Mono', monospace" };

const statusColors = {
  paid:     { bg: '#1a3a2a', color: '#4ade80' },
  pending:  { bg: '#2a2218', color: '#f59e0b' },
  failed:   { bg: '#3a1a1a', color: '#f87171' },
  refunded: { bg: '#1e1e2e', color: '#6b9fff' },
};

// Terminal payments come from the Windows shop tool; text/email are portal-entered
const methodStyle = {
  terminal: { bg: '#1a3a2a', color: '#4ade80', icon: '💳' },
  text:     { bg: '#2a2218', color: '#f59e0b', icon: '💬' },
  email:    { bg: '#1e1e2e', color: '#6b9fff', icon: '✉' },
};

function StatCard({ label, value, sub, subColor = '#4ade80' }) {
  return (
    <div style={{ background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.5px', color: '#f0ede8' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: subColor, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function PaymentHistory({ shopId, isAdmin, getToken }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [sending, setSending] = useState(null);
  const [message, setMessage] = useState(null);

  const loadPayments = useCallback(() => {
    if (!isAdmin && !shopId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setFetchError(null);
    const url = isAdmin
      ? `${API}/api/payments`
      : `${API}/api/shops/${shopId}/payments`;

    getToken()
      .then(token =>
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      )
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Request failed (${r.status})`);
        }
        return r.json();
      })
      .then(data => {
        setPayments(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        setFetchError(err.message);
        setLoading(false);
      });
  }, [shopId, isAdmin, getToken]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const sendReceipt = async (p) => {
    if (!p.CustomerEmail) {
      setMessage('No email on file for this customer.');
      setTimeout(() => setMessage(null), 4000);
      return;
    }
    setSending(p.PaymentId);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/payments/${p.PaymentId}/receipt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setMessage(`Receipt sent to ${p.CustomerEmail}`);
    } catch (err) {
      setMessage(`Failed to send receipt: ${err.message}`);
    }
    setSending(null);
    setTimeout(() => setMessage(null), 4000);
  };

  const total = payments.reduce((sum, p) => sum + Number(p.Amount), 0);
  const completed = payments.filter(p => p.Status === 'paid').length;
  const pending = payments.filter(p => p.Status === 'pending').length;
  const avg = payments.length ? total / payments.length : 0;

  if (loading) return <div style={{ color: '#555', fontSize: 13, padding: 24 }}>Loading...</div>;
  if (fetchError) return <div style={{ color: '#f87171', fontSize: 13, padding: 24 }}>{fetchError}</div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Revenue" value={`$${total.toFixed(2)}`} sub="all time" subColor="#888" />
        <StatCard label="Payments" value={payments.length} sub={`${completed} paid`} />
        <StatCard label="Avg Ticket" value={`$${avg.toFixed(2)}`} sub="per RO" subColor="#6b9fff" />
        <StatCard label="Pending" value={pending} sub="needs action" subColor={pending > 0 ? '#f59e0b' : '#4ade80'} />
      </div>

      {message && (
        <div style={{ background: '#1a3a2a', color: '#4ade80', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {message}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
        {isAdmin ? 'All Payments' : 'Recent Payments'}
      </div>

      {payments.length === 0 ? (
        <div style={{ color: '#555', fontSize: 13 }}>No payments yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['RO #', ...(isAdmin ? ['Shop'] : []), 'Customer', 'Amount', 'Method', 'Status', 'Date', ''].map(h => (
                <th key={h} style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #1e1e1e' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.PaymentId} style={{ borderBottom: '0.5px solid #1a1a1a' }}>
                <td style={{ padding: 12, ...mono, fontSize: 12, color: '#6b9fff' }}>{p.RONumber}</td>
                {isAdmin && (
                  <td style={{ padding: 12, fontSize: 12, color: '#888' }}>{p.ShopName}</td>
                )}
                <td style={{ padding: 12 }}>
                  <div style={{ fontSize: 13, color: '#f0ede8', fontWeight: 500 }}>{p.CustomerName}</div>
                  <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>{p.CustomerEmail || '—'}</div>
                </td>
                <td style={{ padding: 12, ...mono, fontSize: 13, color: '#f0ede8', fontWeight: 500 }}>${Number(p.Amount).toFixed(2)}</td>
                <td style={{ padding: 12 }}>
                  {(() => {
                    const m = methodStyle[p.PaymentMethod] || { bg: '#1e1e1e', color: '#888', icon: '' };
                    const rrn = p.ValorTransactionId ? `RRN: ${p.ValorTransactionId}` : '';
                    const auth = p.AuthCode ? `Auth: ${p.AuthCode}` : '';
                    const tt = [rrn, auth].filter(Boolean).join('  ') || undefined;
                    return (
                      <span title={tt} style={{ ...mono, fontSize: 11, background: m.bg, color: m.color, padding: '2px 6px', borderRadius: 4, cursor: tt ? 'help' : 'default' }}>
                        {m.icon}{m.icon && ' '}{p.PaymentMethod}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ padding: 12 }}>
                  <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, ...mono, background: (statusColors[p.Status] || statusColors.pending).bg, color: (statusColors[p.Status] || statusColors.pending).color }}>
                    {p.Status === 'paid' ? '● ' : '○ '}{p.Status}
                  </span>
                </td>
                <td style={{ padding: 12, fontSize: 12, color: '#555' }}>{new Date(p.CreatedAt).toLocaleDateString()}</td>
                <td style={{ padding: 12 }}>
                  <button onClick={() => sendReceipt(p)} disabled={sending === p.PaymentId}
                    style={{ background: '#1e1e1e', border: '0.5px solid #2a2a2a', color: '#888', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                    {sending === p.PaymentId ? 'Sending...' : 'Receipt'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
